import React from "react";
import { AlertCircle, ArrowDownRight, ArrowUpRight, Clock, RefreshCw, ShieldCheck } from "lucide-react";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import {
  FinancePageHeader,
  FinanceMetricCard,
  FinanceMetricGrid,
  FinanceSection,
  FinanceSecondaryMetrics,
  FinanceStatusBadge,
  humanizeFinanceStatus,
  FinanceResponsiveTable,
  FinanceLoadingState,
  FinanceErrorState,
} from "@/components/finance/ui";

const euro = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

function formatCents(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return "—";
  return euro.format(cents / 100);
}

export default function FinanceSummaryDashboard() {
  const [, navigate] = useLocation();
  const summaryQuery = trpc.finance.dashboard.summary.useQuery();
  const forecastQuery = trpc.finance.treasury.forecast.useQuery({ horizon: "13w" });
  const integrityQuery = trpc.finance.integrity.checks.useQuery();

  const isLoading = summaryQuery.isLoading || forecastQuery.isLoading || integrityQuery.isLoading;
  const hasError = summaryQuery.isError || forecastQuery.isError || integrityQuery.isError;

  const refetchAll = () => {
    summaryQuery.refetch();
    forecastQuery.refetch();
    integrityQuery.refetch();
  };

  if (isLoading) return <FinanceLoadingState cardsCount={4} />;

  if (hasError) {
    const message = summaryQuery.error?.message || forecastQuery.error?.message || integrityQuery.error?.message;
    return (
      <FinanceErrorState
        title="Error al cargar Finanzas"
        message={message}
        onRetry={refetchAll}
      />
    );
  }

  const summary = summaryQuery.data;
  const forecast = forecastQuery.data;
  const integrity = integrityQuery.data;
  const maxFlow = Math.max(
    1,
    ...(forecast?.buckets ?? []).map((bucket) => Math.max(bucket.collectionsCents, bucket.paymentsCents))
  );

  const allCards = summary?.cards ?? [];
  // Max 4 primary KPI cards on first row desktop
  const primaryCards = allCards.slice(0, 4);
  const secondaryCards = allCards.slice(4);

  const secondaryMetricsItems = secondaryCards.map((card) => ({
    key: card.key,
    label: card.title,
    value: formatCents(card.amountCents),
    count: card.count,
    onClick: () => navigate(card.actionPath),
  }));

  return (
    <div className="space-y-6">
      <FinancePageHeader
        eyebrow="Finanzas y Contabilidad"
        eyebrowIcon={ShieldCheck}
        title="Resumen financiero"
        description="KPIs operativos, previsión parcial de caja e integridad financiera sobre fuentes reales actuales."
        updatedAt={summary?.updatedAt}
        primaryAction={{
          label: "Actualizar",
          icon: RefreshCw,
          onClick: refetchAll,
          variant: "outline",
        }}
        note={
          summary?.coverage.partial
            ? {
                title: "Nota de cobertura de datos",
                message: summary.coverage.note,
                missingSources: summary.coverage.missingSources,
                type: "amber",
              }
            : undefined
        }
      />

      {/* Max 4 primary KPI cards in first row desktop */}
      <FinanceMetricGrid maxColumns={4}>
        {primaryCards.map((card) => (
          <FinanceMetricCard
            key={card.key}
            title={card.title}
            amountCents={card.amountCents}
            count={card.count}
            description={card.description}
            emptyState={card.emptyState}
            source={card.includedStatuses.map(humanizeFinanceStatus)}
            onAction={() => navigate(card.actionPath)}
            actionLabel="Ver"
          />
        ))}
      </FinanceMetricGrid>

      {/* Secondary metrics rendered as compact chips band */}
      {secondaryMetricsItems.length > 0 && (
        <FinanceSecondaryMetrics
          title="Otras métricas del resumen"
          metrics={secondaryMetricsItems}
        />
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.8fr)]">
        <FinanceSection
          title="Cash flow 13 semanas"
          description="Cobros y pagos previstos con cobertura parcial. Saldo proyectado pendiente de saldo inicial real."
          badge={<Badge variant="outline" className="text-xs font-normal">13 semanas</Badge>}
        >
          <div className="space-y-3">
            {forecast?.buckets.map((bucket) => (
              <div key={bucket.key} className="grid gap-2 rounded-md border border-border/80 p-3 md:grid-cols-[100px_minmax(0,1fr)_130px] md:items-center bg-card shadow-2xs">
                <div>
                  <p className="text-xs font-semibold">{bucket.label}</p>
                  <p className="text-[11px] text-muted-foreground">{bucket.startsOn}</p>
                </div>
                <div className="space-y-1.5">
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${Math.max(2, (bucket.collectionsCents / maxFlow) * 100)}%` }}
                    />
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-rose-500 rounded-full"
                      style={{ width: `${Math.max(2, (bucket.paymentsCents / maxFlow) * 100)}%` }}
                    />
                  </div>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center justify-between gap-2 text-emerald-700 dark:text-emerald-400 font-medium">
                    <ArrowUpRight className="h-3.5 w-3.5" />
                    <span className="tabular-nums">{formatCents(bucket.collectionsCents)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-rose-700 dark:text-rose-400 font-medium">
                    <ArrowDownRight className="h-3.5 w-3.5" />
                    <span className="tabular-nums">{formatCents(bucket.paymentsCents)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 font-semibold pt-0.5 border-t border-border/40">
                    <span>Neto</span>
                    <span className="tabular-nums">{formatCents(bucket.netCents)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </FinanceSection>

        <FinanceSection
          title="Integridad"
          description="Señales operativas que afectan a la calidad del dato financiero."
        >
          <FinanceResponsiveTable>
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-normal">Control</TableHead>
                  <TableHead className="w-16 whitespace-normal text-right">Casos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {integrity?.checks.map((check) => (
                  <TableRow key={check.key}>
                    <TableCell className="min-w-0 whitespace-normal break-words py-2.5">
                      <button
                        className="block w-full min-w-0 whitespace-normal break-words text-left text-xs font-semibold text-primary hover:underline"
                        onClick={() => navigate(check.actionPath)}
                      >
                        {check.label}
                      </button>
                      <div className="mt-1 flex items-center gap-2">
                        <FinanceStatusBadge status={check.severity} />
                      </div>
                    </TableCell>
                    <TableCell className="w-16 whitespace-normal py-2.5 text-right text-xs font-bold tabular-nums">{check.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </FinanceResponsiveTable>

          <div className="mt-4 rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
              <Clock className="h-3.5 w-3.5 text-primary" />
              <span>Cobertura actual</span>
            </div>
            <span>Fuentes incluidas: {summary?.coverage.includedSources.join(", ")}.</span>
          </div>
        </FinanceSection>
      </div>
    </div>
  );
}
